import type { Ref, ShallowReactive, Reactive } from 'vue'

export const useValueSynced2 = <T>(
  watchFunc: () => T,
  options?: {
    deep?: boolean
    enabled?: (next: T, prev: T | undefined) => boolean
    unproxy?: (val: T) => T
  },
): [value: Readonly<Ref<T>>, set: (i: T) => void, reset: () => void] => {
  const fix = options?.unproxy || unproxy

  const holder = shallowRef<T>(fix(watchFunc()))

  function set(i: T) {
    holder.value = i
  }

  function enabled(curr: T, prev: T | undefined) {
    if (curr === prev) return false
    return !options?.enabled ? true : options.enabled(curr, prev)
  }

  watch(
    () => watchFunc(),
    (i) => {
      if (enabled(i, holder.value)) set(fix(i))
    },
    { deep: options?.deep },
  )

  function reset() {
    set(fix(watchFunc()))
  }

  return [toRef(() => holder.value), set, reset]
}

function reactiveSync<T extends Record<string, unknown>, Base extends (T | Reactive<T>)>(data: Base, cb: () => T) {
  watch(
    () => cb(),
    (on) => {
      for (const k in on) {
        const value = on[k]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (data[k] !== value && k in data) data[k] = value as unknown as any
      }
    },
  )
  return data
}

export function useReactiveSync<T extends Record<string, unknown>>(cb: () => T): Reactive<T> {
  return reactiveSync(reactive(cb()), cb)
}

export function useShallowReactiveSync<T extends Record<string, unknown>>(cb: () => T): ShallowReactive<T> {
  return reactiveSync(shallowReactive(cb()), cb)
}
